import { alpha } from "@mui/material";

interface Permission {
  _id: string;
  name: string;
}

export interface PermissionGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  permissions: {
    id: string;
    name: string;
    description: string;
  }[];
}

const getGroupIcon = (groupName: string): string => {
  const icons: { [key: string]: string } = {
    dashboard: "dashboard",
    staff: "people",
    roles: "admin_panel_settings",
    inventory: "inventory",
    menu: "restaurant_menu",
    pos: "point_of_sale",
    customers: "group",
    reports: "analytics",
    settings: "settings",
    facilities: "store",
    suppliers: "handshake",
    configuration: "admin_panel_settings",
  };
  return icons[groupName] || "extension";
};

const getGroupDescription = (groupName: string): string => {
  const descriptions: { [key: string]: string } = {
    dashboard: "Access to dashboard and analytics",
    staff: "Manage staff members and their access",
    roles: "Manage roles and permissions",
    inventory: "Manage inventory and stock",
    menu: "Manage menu items and recipes",
    pos: "Point of sale operations",
    customers: "Customer management and loyalty",
    reports: "View and generate reports",
    settings: "System settings and configuration",
    facilities: "Manage branches and warehouses",
    suppliers: "Manage suppliers and orders",
    configuration: "System configuration and settings",
  };
  return descriptions[groupName] || `Manage ${groupName} module`;
};

const getActionDescription = (action: string): string => {
  const descriptions: { [key: string]: string } = {
    view: "View and access",
    create: "Create new items",
    edit: "Modify existing items",
    delete: "Remove items",
    manage: "Full management access",
    export: "Export data",
    import: "Import data",
  };
  return descriptions[action] || action;
};

export const formatPermissionName = (name: string): string => {
  const [group, action] = name.split(".");
  if (!action) return name;

  const formattedGroup = group.charAt(0).toUpperCase() + group.slice(1);
  const formattedAction = action.charAt(0).toUpperCase() + action.slice(1);

  return `${formattedAction} ${formattedGroup}`;
};

export const getPermissionDescription = (name: string): string => {
  const [group, action] = name.split(".");
  if (!action) return name;

  return `${getActionDescription(action)} in ${group} module`;
};

export const organizePermissionsByGroup = (
  permissions: Permission[]
): PermissionGroup[] => {
  // Group permissions by their module
  const groupedPermissions = permissions.reduce(
    (groups: { [key: string]: Permission[] }, permission) => {
      const [groupName] = permission.name.split(".");
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(permission);
      return groups;
    },
    {}
  );

  // Convert grouped permissions into PermissionGroup format
  return Object.entries(groupedPermissions).map(([groupName, permissions]) => ({
    id: groupName,
    name: groupName.charAt(0).toUpperCase() + groupName.slice(1),
    description: getGroupDescription(groupName),
    icon: getGroupIcon(groupName),
    permissions: permissions.map((permission) => ({
      id: permission._id,
      name: formatPermissionName(permission.name),
      description: getPermissionDescription(permission.name),
    })),
  }));
};

export const getPermissionGroups = (
  permissions: Permission[]
): PermissionGroup[] => {
  return organizePermissionsByGroup(permissions);
};
